/**
 * Where each surface actually lives now that the apps are split across origins.
 *
 * In the Next.js app all three surfaces were routes on one host, so a bounce
 * between them was `redirect("/dashboard")`. The HR workspace and portal are
 * now served by the customer app (app.hrdocket.com) and the admin dashboard by
 * its own (admin.hrdocket.com), so a bounce may be a *cross-origin* navigation
 * that the client router cannot perform.
 *
 * `VIEW_HOME` is kept as the path-only map (still correct within an app);
 * these helpers add the origin so a guard can send someone to a surface that
 * lives on the other host.
 */
import { env } from "./env";
import { VIEW_HOME, VIEW_MODES, type ViewMode } from "./view-mode";

/** The origin that serves each surface. */
export function viewOrigin(view: ViewMode): string {
  return view === "admin" ? env.adminUrl : env.appUrl;
}

/** Absolute URL of a surface's landing route. */
export function viewHomeUrl(view: ViewMode): string {
  return `${viewOrigin(view)}${VIEW_HOME[view]}`;
}

/** Whether a surface is served by the app currently running. */
export function isLocalView(view: ViewMode): boolean {
  if (typeof window === "undefined") return true;
  try {
    return new URL(viewOrigin(view)).origin === window.location.origin;
  } catch {
    return true;
  }
}

/**
 * Sends the browser to a surface's home.
 *
 * Returns the in-app path when the surface is served by this app, so the caller
 * can hand it to the router; returns `null` after starting a full-page
 * navigation when the surface lives on the other origin. `replace` is used so a
 * bounced-away surface doesn't sit in the back-history as a trap.
 */
export function navigateToView(view: ViewMode): string | null {
  if (isLocalView(view)) return VIEW_HOME[view];
  window.location.replace(viewHomeUrl(view));
  return null;
}

/**
 * The surfaces the app currently running actually hosts.
 *
 * This is what turns the shared guard code into per-app behaviour without a
 * per-app constant: the customer app serves `user` + `employee`, the admin app
 * serves `admin`, and each one derives that from its own origin.
 *
 * The empty result is treated as "serves everything". Without that fallback a
 * missing `VITE_APP_URL`/`VITE_ADMIN_URL` would leave an app hosting no surface
 * at all and lock every account out; degrading to the pre-split behaviour is
 * the safer failure.
 */
export function servedViews(): ViewMode[] {
  const served = VIEW_MODES.filter(isLocalView);
  return served.length > 0 ? served : VIEW_MODES;
}

/** Whether this app is the platform-admin dashboard and nothing else. */
export function isAdminApp(): boolean {
  const served = servedViews();
  return served.includes("admin") && !served.includes("user");
}

/**
 * This app's landing path for a session whose capabilities aren't known yet.
 *
 * Used for the `callbackURL`s that have to be fixed before the account's
 * surface can be resolved — the OAuth hop and the verification email — where
 * the old hard-coded `/dashboard` sent the admin app to a route it doesn't
 * have. Once there, the route guard resolves the real surface.
 */
export function appLandingPath(): string {
  const served = servedViews();
  const first = (["user", "employee", "admin"] as ViewMode[]).find((view) => served.includes(view));
  return first ? VIEW_HOME[first] : "/";
}

/** Copy for "this app hosts no surface your account may use". */
export function noSurfaceMessage(): string {
  return isAdminApp()
    ? "This account doesn't have admin access."
    : "This account doesn't have access to this workspace.";
}
